import { useEffect } from 'react';
import { ApiError } from '../lib/api/client.js';
import { describeError } from '../lib/errors.js';

interface Props {
  error: unknown;
  /** Offered only when trying again could help; omit for errors the person can't retry from here. */
  onRetry?: (() => void) | undefined;
  /** A short bold lead, for when the message alone doesn't say what failed. */
  title?: string;
}

/**
 * The one way an error reaches the screen.
 *
 * Shows a translated message (see `describeError`), a "Try again" when it can
 * help, and — for errors that came from the API — the request id, with a link
 * to Contact so a report can be traced instead of guessed at. The raw error is
 * logged to the console for developers and never shown.
 *
 * An ingest rejection is the exception to "one message": the API lists every
 * rule that failed at once, which is the whole design of that gate, so each is
 * shown rather than collapsed into a blanket line.
 */
export function ErrorNotice({ error, onRetry, title }: Props) {
  useEffect(() => {
    console.error('[voxfauna] shown to user:', error);
  }, [error]);

  if (error instanceof ApiError && error.isIngestRejection && error.details?.length) {
    return (
      <div className="notice notice--error" role="alert">
        <strong>This observation could not be accepted.</strong>
        <ul>
          {error.details.map((failure, i) => (
            <li key={i}>{failure.message}</li>
          ))}
        </ul>
      </div>
    );
  }

  const view = describeError(error);

  return (
    <div className="notice notice--error" role="alert">
      {title && <strong>{title} </strong>}
      {view.message}
      {onRetry && view.retryable && (
        <>
          {' '}
          <button type="button" className="btn btn--quiet" onClick={onRetry}>
            Try again
          </button>
        </>
      )}
      {view.reference && (
        <span className="notice-ref">
          If it keeps happening, <a href="#/contact">contact us</a> and quote reference{' '}
          <code>{view.reference}</code>.
        </span>
      )}
    </div>
  );
}
